import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowRight, KeyRound, Copy, Check, Loader2, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuccessPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [baseService, setBaseService] = useState('');
  const [license, setLicense] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimDone, setClaimDone] = useState(false);
  const [copied, setCopied] = useState(false);
  const [sessionId] = useState(() => new URLSearchParams(window.location.search).get('session_id'));

  useEffect(() => {
    if (!sessionId || !token) return;

    setClaiming(true);
    // 1) Generic self-healing fulfillment (order + licence + Discord ticket if webhook missed)
    fetch(`${API_URL}/api/orders/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then(async (d) => {
        const bs = (d && d.base_service) || '';
        setBaseService(bs);
        if (bs === 'aqmoptitool') {
          if (d && d.license_key) {
            setLicense(d.license_key);
          } else {
            // Already processed by webhook — retrieve the key (idempotent upsert)
            try {
              const r2 = await fetch(`${API_URL}/api/aqmoptitool/claim-license`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ session_id: sessionId }),
              });
              if (r2.ok) {
                const d2 = await r2.json();
                if (d2 && d2.license_key) setLicense(d2.license_key);
              }
            } catch (_) {}
          }
        }
      })
      .catch(() => {})
      .finally(() => { setClaiming(false); setClaimDone(true); });
  }, [token, sessionId]);

  const copyLicense = () => {
    if (!license) return;
    navigator.clipboard?.writeText(license);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const isAqmTool = baseService === 'aqmoptitool';
  const isLicence = baseService.startsWith('licence');

  // ── AqmOptiTool purchase — software license (no Windows install flow) ──────
  if (isAqmTool) {
    return (
      <div className="min-h-screen bg-[#0a0b14] flex items-center justify-center p-4">
        <div className="max-w-2xl w-full text-center">
          <div className="mb-8 flex justify-center">
            <div className="w-24 h-24 rounded-full bg-green-600/20 flex items-center justify-center">
              <CheckCircle className="w-16 h-16 text-green-400" />
            </div>
          </div>

          <h1 className="text-4xl font-bold text-white mb-4">Paiement Réussi! 🎉</h1>
          <p className="text-xl text-gray-300 mb-8">Merci pour ton achat AqmOptiTool Premium !</p>

          {license ? (
            <div className="mt-2 mb-8 rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-500/[0.08] to-transparent p-6 text-left" data-testid="success-license-card">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-cyan-500/15 border border-cyan-500/30 flex items-center justify-center">
                  <KeyRound className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <h3 className="text-white font-semibold text-sm">Ta licence AqmOptiTool est prête ✅</h3>
                  <p className="text-gray-400 text-xs">Colle cette clé dans le logiciel pour l&apos;activer.</p>
                </div>
              </div>
              <div className="flex items-center gap-2 bg-black/40 border border-zinc-700 rounded-lg px-3 py-2.5">
                <code className="flex-1 min-w-0 text-cyan-300 text-sm font-mono truncate select-all" data-testid="success-license-key">{license}</code>
                <button
                  onClick={copyLicense}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-300 text-xs font-medium transition-colors"
                  data-testid="success-license-copy-btn"
                >
                  {copied ? <><Check className="w-3.5 h-3.5" /> Copié</> : <><Copy className="w-3.5 h-3.5" /> Copier</>}
                </button>
              </div>
              <p className="text-gray-500 text-[11px] mt-3">Tu peux la retrouver à tout moment dans <span className="text-cyan-400">Compte → Ma Licence</span>.</p>
            </div>
          ) : claiming || !claimDone ? (
            <div className="flex items-center justify-center gap-2 text-cyan-400 text-sm mb-8" data-testid="license-claiming">
              <Loader2 className="w-4 h-4 animate-spin" />
              Génération de ta licence…
            </div>
          ) : (
            <div className="mb-8 rounded-2xl border border-amber-500/30 bg-amber-500/[0.06] p-6 text-left" data-testid="success-license-pending">
              <div className="flex items-center gap-2 mb-2">
                <AlertCircle className="w-5 h-5 text-amber-400" />
                <h3 className="text-white font-semibold text-sm">Ta licence est en cours de création</h3>
              </div>
              <p className="text-gray-400 text-xs">
                Va dans <span className="text-cyan-400">Compte → Ma Licence</span> dans quelques instants — ta clé y apparaîtra. Si ce n&apos;est pas le cas, contacte le support sur Discord.
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/ma-licence')} className="bg-cyan-600 hover:bg-cyan-700 text-white">
              Voir ma licence
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="border-cyan-700 text-cyan-400 hover:bg-cyan-950/20">
              Retour à l&apos;accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Neutral loading while we detect the purchase type ─────────────────────
  if (sessionId && token && !claimDone) {
    return (
      <div className="min-h-screen bg-[#0a0b14] flex items-center justify-center p-4">
        <div className="flex items-center gap-3 text-cyan-400" data-testid="success-loading">
          <Loader2 className="w-6 h-6 animate-spin" />
          <span className="text-lg">Vérification de ta commande…</span>
        </div>
      </div>
    );
  }

  // ── Licence AqmOpti purchase — uses credited, no Windows ticket flow ──────
  if (isLicence) {
    return (
      <div className="min-h-screen bg-[#0a0b14] flex items-center justify-center p-4">
        <div className="max-w-2xl w-full text-center">
          <div className="mb-8 flex justify-center">
            <div className="w-24 h-24 rounded-full bg-green-600/20 flex items-center justify-center">
              <CheckCircle className="w-16 h-16 text-green-400" />
            </div>
          </div>

          <h1 className="text-4xl font-bold text-white mb-4">Paiement Réussi! 🎉</h1>
          <p className="text-xl text-gray-300 mb-8">Tes utilisations ont été ajoutées à ta licence AqmOpti !</p>

          <div className="bg-cyan-950/30 border border-cyan-800/30 rounded-xl p-6 mb-8 text-left" data-testid="success-licence-info">
            <h3 className="text-cyan-400 font-semibold text-lg mb-3 flex items-center gap-2">
              <KeyRound className="w-5 h-5" /> Récupérer ta clé
            </h3>
            <ul className="text-gray-300 space-y-2 text-sm">
              <li className="flex items-start gap-2">
                <span className="text-cyan-400 mt-0.5">✓</span>
                <span>Utilise la commande <code className="text-cyan-300 bg-black/40 px-1.5 py-0.5 rounded">/cle</code> sur le serveur Discord pour récupérer ta clé</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400 mt-0.5">✓</span>
                <span>Ta licence est liée à ton compte Discord — les utilisations se cumulent automatiquement</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-cyan-400 mt-0.5">✓</span>
                <span>Retrouve-la aussi dans <span className="text-cyan-400">Compte → Ma Licence</span></span>
              </li>
            </ul>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/ma-licence')} className="bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="goto-ma-licence-btn">
              Voir ma licence
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Button onClick={() => navigate('/')} variant="outline" className="border-cyan-700 text-cyan-400 hover:bg-cyan-950/20">
              Retour à l&apos;accueil
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ── Standard service (Windows / BIOS / Pack) ───────────────────────────────
  return (
    <div className="min-h-screen bg-[#0a0b14] flex items-center justify-center p-4">
      <div className="max-w-2xl w-full text-center">
        <div className="mb-8 flex justify-center">
          <div className="w-24 h-24 rounded-full bg-green-600/20 flex items-center justify-center">
            <CheckCircle className="w-16 h-16 text-green-400" />
          </div>
        </div>

        <h1 className="text-4xl font-bold text-white mb-4">Paiement Réussi! 🎉</h1>
        <p className="text-xl text-gray-300 mb-2">Merci pour votre confiance!</p>
        <p className="text-gray-400 mb-8">
          Votre commande a été validée et votre ticket Discord a été créé automatiquement.
        </p>

        <div className="bg-cyan-950/30 border border-cyan-800/30 rounded-xl p-6 mb-8">
          <h3 className="text-cyan-400 font-semibold text-lg mb-3">📋 Prochaines Étapes</h3>
          <ul className="text-left text-gray-300 space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-cyan-400 mt-1">✓</span>
              <span>Un ticket Discord a été créé avec votre commande</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-400 mt-1">✓</span>
              <span>Notre équipe vous contactera très rapidement</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-cyan-400 mt-1">✓</span>
              <span>Suivez les tutoriels et indiquez quand vous avez terminé</span>
            </li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={() => navigate('/espace-client')} className="bg-cyan-600 hover:bg-cyan-700 text-white">
            Voir les tutoriels
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button onClick={() => navigate('/')} variant="outline" className="border-cyan-700 text-cyan-400 hover:bg-cyan-950/20">
            Retour à l&apos;accueil
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage;
